/**
 * Dock-grid → CSV (pure; shared by Dock.downloadCsv and the harness).
 *
 * WYSIWYG: callers pass the grid's VISIBLE columns (perspective-curated, user-reordered,
 * empty-hidden) and visible rows — the CSV is exactly the table on screen. Self-description
 * rides as `# `-prefixed comment lines above the header (export title + the provenance line:
 * perspective, KPI chips, search/facets, window, grouping) so a downloaded file can always
 * say what produced it; ingestion that minds them can skip rows starting with '#'.
 */
export interface CsvColumn<Row> { header: string; value: (r: Row) => string | number | null; key?: string }

// Lead with a UTF-8 BOM. The export carries non-ASCII punctuation (—, ·, →) and Windows Excel
// decodes a BOM-less file as the legacy code page → mojibake; the BOM rides on the leading `#`
// comment line, so ingestion that skips '#' rows never sees it as data. (Mac/Sheets are fine either way.)
const BOM = '\ufeff';

export function buildCsv<Row>(columns: CsvColumn<Row>[], rows: Row[], commentLines: string[] = []): string {
  const esc = (v: unknown) => {
    // CSV formula injection: a cell beginning with = + - @ TAB or CR executes when the file opens
    // in Excel/Sheets. Buyer/agent names, permit descriptions and GHL text all flow in here, so
    // neutralize with a leading single quote BEFORE the comma/quote/newline quoting below.
    let s = v == null ? '' : String(v);
    if (/^[=+\-@\t\r]/.test(s)) s = `'${s}`;
    // \r joins \n/,/" as a character that forces field quoting (a bare CR can break a row).
    return /[",\n\r]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
  };
  const comments = commentLines.map(s => (s || '').trim()).filter(Boolean).map(s => esc(`# ${s}`));
  const head = columns.map(c => esc(c.header)).join(',');
  const body = rows.map(r => columns.map(c => esc(c.value(r))).join(',')).join('\n');
  return BOM + [...comments, head, body].join('\n');
}

// Render money-keyed columns with uniform 2-dp and no thousands separator (3274.40, 0.00) — analysis-
// parseable AND finance-uniform. CSV-only: the styled XLSX keeps the raw number for its currency numFmt.
export function withCsvMoney<Row>(columns: CsvColumn<Row>[], moneyKeys: readonly string[]): CsvColumn<Row>[] {
  const set = new Set(moneyKeys);
  return columns.map(c => (c.key && set.has(c.key))
    ? { ...c, value: (r: Row) => { const v = c.value(r); return typeof v === 'number' ? v.toFixed(2) : v; } }
    : c);
}
